import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Check,
  ChevronsUpDown,
  Sparkle,
  Loader,
  Upload,
  X,
} from 'lucide-react';
import { cn } from 'lib/utils';
import { TBrandBookFormData } from '../context/index';
import { blogService } from 'features/ai-blog-writer/services/bolgUtilsApi';
import brandbookService from '../services/brandbookApi';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { gifService } from '../../gif-maker/services/gifMakerApi';
import DualRangeSlider from 'components/ui/dual-range-slider';
import { BrandbookData } from '../types';
import { useBrandBookContext } from '../context/index';
import { BrandBookFormSchema } from '../context/index';
import { z } from 'zod';

interface FormErrors {
  companyName?: string;
  industry?: string;
  differentiators?: string;
  description?: string;
  age?: string;
  gender?: string;
  location?: string;
  logoType?: string;
  targetAudience?: string;
}

export default function GenerateBrandBookForm() {
  const [open, setOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const { setFormDetails, formDetails } = useBrandBookContext();
  const navigate = useNavigate();
  const [customIndustry, setCustomIndustry] = useState('');
  const [customTargetAudience, setCustomTargetAudience] = useState('');
  const [showCustomIndustryInput, setShowCustomIndustryInput] = useState(false);
  const [showCustomTargetAudienceInput, setShowCustomTargetAudienceInput] = useState(false);

  const { data: industriesData, isLoading: industriesLoading } = useQuery({
    queryKey: ['fetch-all-industries'],
    queryFn: blogService.getAllIndustries,
  });

  const { data: countriesData, isLoading: countriesLoading } = useQuery({
    queryKey: ['fetch-all-countries'],
    queryFn: async () => {
      const res = await axios.get(
        'https://restcountries.com/v3.1/all?fields=name'
      );

      return [{ name: { common: 'All' } }, ...res.data];
    },
  });

  const form = useForm<TBrandBookFormData>({
    resolver: zodResolver(BrandBookFormSchema),
    defaultValues: {
      logoType: 'generate',
      age: [1, 100],
      targetAudience: '',
    },
  });

  const validateForm = (formData: TBrandBookFormData) => {
    const newErrors: FormErrors = {};

    // Company name validation
    if (!formData.companyName?.trim()) {
      newErrors.companyName = 'Brand name is required';
    } else if (formData.companyName.length < 2) {
      newErrors.companyName = 'Brand name must be at least 2 characters long';
    }

    // Industry validation
    if (!formData.industry) {
      newErrors.industry = 'Industry is required';
    }

    // Differentiators validation
    if (!formData.differentiators?.trim()) {
      newErrors.differentiators = 'USP is required';
    } else if (formData.differentiators.length < 10) {
      newErrors.differentiators = 'USP must be at least 10 characters long';
    }

    // Description validation
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 20) {
      newErrors.description = 'Description must be at least 20 characters long';
    }

    // Age validation
    if (!formData.age) {
      newErrors.age = 'Age is required';
    } else {
      const ageNum = formData.age;
      if (
        isNaN(ageNum[0]) ||
        isNaN(ageNum[1]) ||
        ageNum[0] < 0 ||
        ageNum[1] > 100
      ) {
        newErrors.age = 'Age must be between 1 and 100';
      }
    }

    // Gender validation
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }
    // Target Audience validation
    if (!formData.targetAudience) {
      newErrors.targetAudience = 'Target audience is required';
    }
    // Location validation
    if (!formData.location) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedLogo(file);
    }
  };

  const handleRemoveLogo = () => {
    setSelectedLogo(null);
  };

  const handleSubmit = async (data: z.infer<typeof BrandBookFormSchema>) => {
    // Create a copy of the form data
    const formData = { ...data };
    
    // If custom industry is selected, use the custom value
    if (showCustomIndustryInput && customIndustry.trim()) {
      formData.industry = customIndustry.trim();
    }
    
    // If custom target audience is selected, use the custom value
    if (showCustomTargetAudienceInput && customTargetAudience.trim()) {
      formData.targetAudience = customTargetAudience.trim();
    }
    
    // Check if any required fields are empty
    if (
      !formData.companyName?.trim() ||
      !formData.industry?.trim() ||
      !formData.differentiators?.trim() ||
      !formData.description?.trim() ||
      !formData.age?.toString()?.trim() ||
      !formData.gender?.trim() ||
      !formData.location?.trim()
    ) {
      toast.error('Please fill all fields');
      return;
    }

    if (!validateForm(formData)) {
      toast.error('Please fix the form errors before generating');
      return;
    }

    try {
      setIsSubmitting(true);
      let logoUrl;

      if (formData.logoType === 'upload' && selectedLogo) {
        try {
          logoUrl = await gifService.generateImageUrl(selectedLogo);
        } catch (error) {
          console.error('Error uploading logo:', error);
          toast.error('Error uploading logo');
          return;
        }
      }

      const requestData = {
        companyName: formData.companyName,
        description: formData.description,
        industry: formData.industry,
        differentiators: formData.differentiators,
        targetedAudience: formData.targetAudience,
        gender: formData.gender,
        age: `${formData.age[0]}-${formData.age[1]}`,
        location: formData.location,
        logo: logoUrl || null,
      };
      // @ts-expect-error response is not null
      const response = await brandbookService.generateBrandbook(requestData);

      if (response.success) {
        const { brandbook } = response.data
        
        // Transform the API response to match our context structure
        const transformedBrandbook: BrandbookData = {
          slogan: brandbook.slogan,
          brandValues: brandbook.brandValues,
          vision: brandbook.vision,
          voice: brandbook.voice,
          productService: brandbook.productService,
          colorPalette: brandbook.colorPalette,
          suggestedIcon: brandbook.suggestedIcon,
          suggestedFont: brandbook.suggestedFont,
          logo_url: brandbook.logo_url?.[0] || '',
          companyName: brandbook.companyName,
          targetAudience: formData.targetAudience
        };

        // Update context with both form data and transformed brandbook
        setFormDetails({
          ...formData,
          brandbook: transformedBrandbook
        });
        console.log(formDetails);
        navigate('/brandbook/result');
      } else {
        toast.error('Failed to generate brandbook');
      }
    } catch (error) {
      console.error('Error generating brandbook:', error);
      toast.error('Failed to generate brandbook');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <p className="uppercase text-xs">Start</p>
      <p className="text-xl sm:text-2xl font-medium">Enter brand details</p>

      <form
        className="bg-background mt-4 p-4 sm:p-8 rounded-lg flex flex-col gap-4"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="companyName">Brand Name</Label>
            <Input
              type="text"
              placeholder="Enter your brand name"
              {...form.register('companyName')}
              className={errors.companyName ? 'border-red-500' : ''}
            />
            {errors.companyName && (
              <span className="text-red-500 text-sm">{errors.companyName}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="industry">Industry</Label>
            <Controller
              control={form.control}
              name="industry"
              render={({ field }) => (
                <Select
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      setShowCustomIndustryInput(true);
                      // Set the form field value to 'custom' temporarily
                      field.onChange('custom');
                    } else {
                      setShowCustomIndustryInput(false);
                      field.onChange(value);
                    }
                  }}
                  defaultValue={field.value}
                >
                  <SelectTrigger
                    className={errors.industry ? 'border-red-500' : ''}
                  >
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {industriesLoading
                      ? 'Loading...'
                      : industriesData?.data?.map((industry) => (
                          <SelectItem key={industry.id} value={industry.name}>
                            {industry.name}
                          </SelectItem>
                        ))}
                    <SelectItem value="custom">Custom (enter your own)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.industry && (
              <span className="text-red-500 text-sm">{errors.industry}</span>
            )}
            
            {/* Show custom input field when Custom is selected */}
            {showCustomIndustryInput && (
              <div className="mt-2">
                <Input
                  placeholder="Enter your industry"
                  value={customIndustry}
                  onChange={(e) => setCustomIndustry(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        {/* USP and Description Fields */}
        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="differentiators">
              Competitive Differentiator (USP)
            </Label>
            <Input
              type="text"
              placeholder="Enter your brand's USP here"
              {...form.register('differentiators')}
              className={errors.differentiators ? 'border-red-500' : ''}
            />
            {errors.differentiators && (
              <span className="text-red-500 text-sm">
                {errors.differentiators}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="description">Brand Description</Label>
            <Input
              type="text"
              placeholder="Tell us more about your brand"
              {...form.register('description')}
              className={errors.description ? 'border-red-500' : ''}
            />
            {errors.description && (
              <span className="text-red-500 text-sm">{errors.description}</span>
            )}
          </div>
        </div>

        {/* Target Audience Section */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="targetAudience">Target Audience</Label>
            <Controller
              control={form.control}
              name="targetAudience"
              render={({ field: { value, onChange } }) => (
                <Select 
                  value={value} 
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      setShowCustomTargetAudienceInput(true);
                      // Set the form field value to 'custom' temporarily
                      onChange('custom');
                    } else {
                      setShowCustomTargetAudienceInput(false);
                      onChange(value);
                    }
                  }}
                >
                  <SelectTrigger
                    className={errors.targetAudience ? 'border-red-500' : ''}
                  >
                    <SelectValue placeholder="Select target audience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B2C">
                      B2C (Direct to Consumer)
                    </SelectItem>
                    <SelectItem value="B2B">
                      B2B (Business to Business)
                    </SelectItem>
                    <SelectItem value="B2B2C">
                      B2B2C (Business through Business to Consumer)
                    </SelectItem>
                    <SelectItem value="D2C">
                      D2C (Direct to Consumer)
                    </SelectItem>
                    <SelectItem value="C2C">
                      C2C (Consumer to Consumer)
                    </SelectItem>
                    <SelectItem value="custom">Custom (enter your own)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.targetAudience && (
              <span className="text-red-500 text-sm">
                {errors.targetAudience}
              </span>
            )}
            
            {/* Show custom input field when Custom is selected */}
            {showCustomTargetAudienceInput && (
              <div className="mt-2">
                <Input
                  placeholder="Describe your target audience"
                  value={customTargetAudience}
                  onChange={(e) => setCustomTargetAudience(e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="geneder">Gender</Label>
            <Controller
              control={form.control}
              name="gender"
              render={({ field }) => (
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <SelectTrigger
                    className={errors.gender ? 'border-red-500' : ''}
                  >
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Both">Both</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.gender && (
              <span className="text-red-500 text-sm">{errors.gender}</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="location">Location</Label>
            <Controller
              control={form.control}
              name="location"
              render={({ field }) => (
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={open}
                      className={cn(
                        'justify-between w-full text-[12px] border border-gray-200',
                        field.value ? 'text-text-primary' : 'text-text-primary',
                        errors.location ? 'border-red-500' : ''
                      )}
                    >
                      {field.value ? field.value : 'Select'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full sm:w-[200px] p-0">
                    <Command>
                      <CommandInput
                        className="outline-none border-0 p-0 focus-within:ring-0"
                        placeholder="Search"
                      />
                      <CommandList>
                        <CommandEmpty>No location found.</CommandEmpty>
                        <CommandGroup>
                          {countriesLoading
                            ? 'Loading...'
                            : countriesData?.map((country: any) => (
                                <CommandItem
                                  key={country.name.common}
                                  value={country.name.common}
                                  onSelect={(currentValue) => {
                                    field.onChange(
                                      currentValue === field.value
                                        ? ''
                                        : currentValue
                                    );
                                    setOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      field.value === country.name.common
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                    )}
                                  />
                                  {country.name.common}
                                </CommandItem>
                              ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            />
            {errors.location && (
              <span className="text-red-500 text-sm">{errors.location}</span>
            )}
          </div>
        </div>

        <Controller
          control={form.control}
          name="age"
          render={({ field }) => (
            <DualRangeSlider
              values={field.value}
              minValue={0}
              maxValue={100}
              onChange={(values) => field.onChange(values)}
            />
          )}
        />

        {/* Logo Section */}
        {/* <div className="mt-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="logoType">Logo</Label>
            <Controller
              control={form.control}
              name="logoType"
              render={({ field }) => (
                <RadioGroup
                  className="flex flex-wrap gap-4"
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem
                      className="border-primaryAccent-9 text-primaryAccent-9"
                      value="generate"
                      id="generate"
                    />
                    <Label htmlFor="generate">Generate logo</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem
                      value="upload"
                      id="upload"
                      className="border-primaryAccent-9 text-primaryAccent-9"
                    />
                    <Label htmlFor="upload">Upload logo</Label>
                  </div>
                </RadioGroup>
              )}
            />

            {form.watch('logoType') === 'upload' && (
              <div className="mt-2">
                {selectedLogo ? (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="flex text-sm items-center gap-2 justify-between border-2 border-dashed border-primaryAccent-5 text-primaryAccent-9 text-[17px] rounded-md p-2 hover:bg-primaryAccent-5 hover:text-white w-full"
                  >
                    <span className="truncate">{selectedLogo.name}</span>
                    <X size={16} className="flex-shrink-0" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="relative text-sm flex items-center justify-center gap-2 w-full border-2 border-dashed border-primaryAccent-5 text-primaryAccent-9 text-[17px] rounded-md p-2 hover:bg-primaryAccent-5 hover:text-white"
                  >
                    <label
                      htmlFor="fileInput"
                      className="absolute inset-0 cursor-pointer"
                    >
                      <input
                        id="fileInput"
                        type="file"
                        accept="image/png, image/jpeg, image/jpg"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </label>
                    <Upload size={16} /> Upload Logo
                  </button>
                )}
              </div>
            )}
          </div>
        </div> */}

        <Button
          variant="customPrimary"
          type="submit"
          disabled={
            isSubmitting ||
            (form.watch('logoType') === 'upload' && !selectedLogo)
          }
          className="w-full mt-4 h-12 px-6 text-lg"
        >
          {isSubmitting ? (
            <Loader className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Sparkle className="w-4 h-4 mr-2" />
          )}
          {isSubmitting ? 'Generating Brandbook...' : 'Generate Brandbook'}
        </Button>
      </form>
    </div>
  );
}
